import { Fragment } from 'react';
import { Star } from 'lucide-react';
import { useFavoriteToggle } from '../../../hooks/favorite/useFavoriteToggle';
import { normalizeGenderLabel } from '../../../../core/utils/format';

/* 질환군 행 노출 스위치 — 백엔드 "질환군명 화면 미노출" 원칙과 충돌(안내API 검토회신 3번).
   숨기려면 false. 데이터는 diseaseGroups prop 하나로만 들어온다. */
const SHOW_DISEASE_ROW = true;

const formatVisitDate = patient => {
  const raw = patient?.last_visit_date || patient?.last_visit || patient?.last_visit_at;
  return raw ? String(raw).slice(0, 10).replaceAll('-', '.') : '';
};

/* Figma 767:26730 Label — w60 bg #f4f4f5 px8 py4 M14 #71717a, border-b */
const Label = ({ children }) => (
  <div className="w-[60px] shrink-0 self-stretch overflow-hidden border-b border-[#e4e4e7] bg-[#f4f4f5] px-2 py-1">
    <p className="truncate text-14 font-medium tracking-[-0.28px] text-[#71717a]">{children}</p>
  </div>
);

const Cell = ({ className = '', children }) => (
  <div className={`flex h-full min-w-0 items-center px-2 ${className}`}>
    <p className="w-full truncate text-14 tracking-[-0.28px]">{children}</p>
  </div>
);

/**
 * Figma 767:26703 data/user/detail — pt24 px24 pb16 gap12
 * @param diseaseGroups string[] 질환군명
 * @param medications [{ name, atc, schedule }] 최근 처방 약 (약 1개 = 1줄)
 */
export const PatientHeader = ({ patient, diseaseGroups = [], medications = [] }) => {
  const uid = patient?.user_id ?? patient?.id;
  const { isFavorite, toggle } = useFavoriteToggle(uid);
  const favorite = patient?.is_favorite ?? isFavorite;
  const visitDate = formatVisitDate(patient);
  const meta = [
    normalizeGenderLabel(patient?.gender) || patient?.gender,
    patient?.age != null ? `${patient.age}세` : '',
    visitDate ? `최근 방문 ${visitDate}` : '',
  ].filter(Boolean);
  const showDisease = SHOW_DISEASE_ROW && diseaseGroups.length > 0;
  const meds = medications.length > 0 ? medications : [{ name: '-', atc: '', schedule: '' }];

  return (
    <header className="flex flex-col gap-3 px-6 pt-6 pb-4">
      {/* 767:26704 patient — 이름 B20 + 별 / 오른쪽 메타 M14 */}
      <div className="flex w-full items-center gap-1">
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <span className="truncate text-20 font-bold tracking-[-0.4px] text-[#18181b]">
            {patient?.name || '-'}
          </span>
          <button
            type="button"
            onClick={toggle}
            aria-label={favorite ? '즐겨찾기 해제' : '즐겨찾기 추가'}
            className="flex size-5 shrink-0 items-center justify-center"
          >
            <Star
              size={18}
              strokeWidth={1.5}
              className={favorite ? 'fill-amber-400 text-amber-400' : 'text-zinc-300'}
            />
          </button>
        </div>
        {meta.length > 0 && (
          <div className="flex shrink-0 items-center gap-0.5 whitespace-nowrap text-14 font-medium tracking-[-0.28px]">
            {meta.map((m, i) => (
              <Fragment key={m}>
                {i > 0 && <span className="text-[#bcbcc2]">·</span>}
                <span className="text-[#52525b]">{m}</span>
              </Fragment>
            ))}
          </div>
        )}
      </div>

      {/* 767:26728 질환군·처방 표 */}
      <div className="flex w-full flex-col border-t border-[#e4e4e7]">
        {showDisease && (
          <div className="flex w-full items-start">
            <Label>질환군</Label>
            <div className="flex h-7 min-w-0 flex-1 border-b border-[#e4e4e7] bg-white">
              <Cell className="flex-1 font-medium text-[#71717a]">{diseaseGroups.join(', ')}</Cell>
            </div>
          </div>
        )}
        <div className="flex w-full items-start">
          <Label>처방</Label>
          <div className="flex min-w-0 flex-1 flex-col">
            {meds.map((m, i) => (
              <div key={`${m.name}-${i}`} className="flex h-7 w-full items-center border-b border-[#e4e4e7] bg-white">
                <Cell className="min-w-[120px] flex-1 font-medium text-[#3f3f46]">{m.name}</Cell>
                <Cell className="min-w-[120px] flex-1 text-[#71717a]">{m.atc}</Cell>
                <Cell className="max-w-[110px] flex-1 justify-end text-right text-[#71717a]">{m.schedule}</Cell>
              </div>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
};
